// ─────────────────────────────────────────────────────────────────────────
// @realtek/core-theme — public entry point (barrel export)
//
// Consumer apps import components from the package name, e.g.:
//     import { AddFormV1 } from '@realtek/core-theme';
//     import '@realtek/core-theme/style.css';
//
// Anything exported here becomes part of the library's public API.
// ─────────────────────────────────────────────────────────────────────────

// All component + theme styles, bundled into a single dist-lib/style.css
import './styles/library.css';

// ── Cards ──────────────────────────────────────────────────────────────────
export { default as CalendarCard } from './components/cards/CalendarCard.jsx';
export { default as DynamicCards } from './components/cards/DynamicCards.jsx';
export { default as HierarchyUserFilter } from './components/cards/HierarchyUserFilter.jsx';

// ── Components ─────────────────────────────────────────────────────────────
export { default as AddFormV1 } from './components/AddFormV1.jsx';
export { default as AppAlert } from './components/AppAlert.jsx';

export { default as ActivityTab } from './components/detail/ActivityTab.jsx';
export { default as ActivityNotesCard } from './components/detail/ActivityNotesCard.jsx';
export { default as NotesTab } from './components/detail/NotesTab.jsx';
export { resolveActivityMeta } from './components/detail/activityMeta.jsx';
export { default as TimelineGroups } from './components/detail/timeline.jsx';
export { default as AppButton } from './components/AppButton.jsx';
export { default as AssigneeAvatars } from './components/AssigneeAvatars.jsx';
export { default as Breadcrumb } from './components/Breadcrumb.jsx';
export { default as BulkUploadPanel } from './components/upload/BulkUploadPanel.jsx';
export {
  validateBulkFile,
  buildAcceptAttr,
  formatFileSize,
  getFileExtension,
  DEFAULT_ACCEPT as BULK_UPLOAD_DEFAULT_ACCEPT,
  DEFAULT_MAX_SIZE_MB as BULK_UPLOAD_DEFAULT_MAX_SIZE_MB,
} from './components/upload/bulkUploadValidation.js';
export { default as CustomPagination } from './components/CustomPagination.jsx';
export { default as DescriptionBlock } from './components/DescriptionBlock.jsx';
export { default as DetailHeaderCard } from './components/detail/DetailHeaderCard.jsx';
export { default as DetailViewPage } from './components/DetailViewPage.jsx';
export { default as DetailViewV1 } from './components/DetailViewV1.jsx';
export { default as DocumentViewer } from './components/DocumentViewer.jsx';
export { default as DynamicTabs } from './components/DynamicTabs.jsx';
export { default as EditFormV1 } from './components/EditFormV1.jsx';
export { default as ListView } from './components/ListView.jsx';
export { default as ModuleRowActions } from './components/actions/ModuleRowActions.jsx';
export { useModuleActions } from './hooks/useModuleActions.js';
export { default as ParamCardView } from './components/ParamCardView.jsx';
export { default as ParamListView } from './components/ParamListView.jsx';
export { default as StatusBadge } from './components/StatusBadge.jsx';
export { default as StylishDynamicTabs } from './components/StylishDynamicTabs.jsx';
export { default as TemplateEditor } from './components/TemplateEditor.jsx';
export { default as TipTapEditor } from './components/TipTapEditor.jsx';
export { default as ViewSummaryWidget } from './components/ViewSummaryWidget.jsx';
export {
  FinanceHoursGrid,
  FinanceKpiRow,
  FinanceStatusPill,
  FinanceEmptyState,
  FinanceSection,
  FinanceLine,
  FinanceDivider,
  FinanceEquation,
  FinanceWorkflowSteps,
  FinanceHistoryTimeline,
} from './components/finance/FinanceComponents.jsx';
export { default as FinanceListToolbar } from './components/finance/FinanceListToolbar.jsx';
export {
  default as FinanceRecordsTable,
  FinanceAvatar,
  FinanceEmployeeCell,
  FinanceTextCell,
  FinanceMoneyCell,
  FinanceBadge,
  FinanceMarginBadge,
  FinanceRowChevron,
} from './components/finance/FinanceRecordsTable.jsx';
export { default as FinanceTimesheetAnalysis, FinanceDayCell } from './components/finance/FinanceTimesheetAnalysis.jsx';
export * from './components/finance/financeUtils.js';
// Config-driven Finance Approval flow (submissionManager / onboardingManager / cfo)
export { default as FinanceApprovalPanel } from './components/finance/FinanceApprovalPanel.jsx';
export {
  FINANCE_APPROVAL_CONFIG,
  getFinanceApprovalConfig,
  marginIncludesReferral,
  resolveRejectionReason,
  FINANCE_TYPE_SUBMISSION_MANAGER,
  FINANCE_TYPE_ONBOARDING_MANAGER,
  FINANCE_TYPE_CFO,
  FINANCE_TYPE_ONLY_FINANCE,
  FINANCE_TYPE_SUBMISSION_MANAGER_REAPPROVE,
  FINANCE_TYPE_ONBOARDING_MANAGER_REAPPROVE,
} from './components/finance/financeApprovalConfig.js';
export {
  resolveSubmissionFinance,
  getFormGroupsRaw,
  flattenGroups,
  findEmployerRow,
} from './components/finance/financeApprovalData.js';
export {
  resolveFinanceInputs,
  computeFinance,
  resolveAndCompute,
  buildRateHistoryEntries,
  matchLocationTax,
  normalizeContract,
  formatMoney as formatFinanceMoney,
  CONTRACT_W2,
  CONTRACT_C2C,
} from './components/finance/financeApprovalEngine.js';
export {
  ApprovalActionBar,
  buildDayGradient,
  DayLogModal,
  NotesPanel,
  PeriodBadgeTag,
  ProofUploadPreview,
  QuickLogBar,
  QuickLogSelectionBar,
  RejectionReasonModal,
  StatusBadgeTag,
  TimesheetDayCell,
} from './components/timesheet/TimesheetComponents.jsx';
export { default as FileListCard } from './components/detail/FileListCard.jsx';

// ── Hooks (all named exports) ──────────────────────────────────────────────
export { useRecordRefresh } from './hooks/useRecordRefresh.js';
export * from './hooks/useMenuPermission.js';
export * from './hooks/useDropdownFields.js';
export * from './hooks/useDropdownValues.js';
export * from './hooks/useEditForm.js';

// ── App infrastructure (layout / auth / popups / form) ─────────────────────
export { AppShell, Header, Sidebar } from './components/layouts/index.js';
export {
  Login,
  ProjectSelect,
  AuthGuard,
  withAuthGuard,
  CoreThemeAdminApp,
  CoreThemeAdminRoutes,
} from './components/auth/index.js';
export { MailPopup, PreviewPopup, WizardPopup, DynamicPopup, DynamicPopupRoute, AddBatchCandidatePopup, POPUP_CONFIGS, VendorShareDrawer } from './components/popups/index.js';
export { default as UserForm } from './components/form/forms.jsx';
// Public admin page exports include the global application shell so consumers
// always receive the main menu and top header. Use AdminDashboardContent only
// when the host application already renders AppShell itself.
export { default as AdminDashboard } from './pages/admin/AdminPageWithShell.jsx';
export {
  getBgcIntegrationConfig,
  getBgcIntegrationSummary,
  getIntegrationConfig,
  getIntegrationConfigs,
  getIntegrationSummary,
  applyIntegrationStatus,
  saveBgcIntegrationConfig,
  saveIntegrationConfig,
  deleteIntegrationConfig,
  testIntegrationConnection,
} from './services/adminApi';
export { default as AdminDashboardContent } from './pages/admin/AdminDashboard.jsx';

// ── Pages (drop straight into the host app's <Route element={...}>) ─────────
// Only git-TRACKED pages are exported. The flat job/candidate/submission page
// wrappers (src/pages/jobPage.jsx, candidatePage.jsx, submissoin.jsx, ...) are
// intentionally NOT in the repo (kept out of main by the block-src-pages
// policy), so they cannot be bundled. The host app keeps those thin route
// wrappers itself and composes them from the building blocks below
// (AddFormV1 / EditFormV1 / ListView / DetailViewV1 / JobsListPage / ...).
//
// EVERY page exported below is wrapped in withAuthGuard(), so a host app can
// drop <AdminPage /> straight onto a route and — if it's opened without a
// session — it shows the "you haven't logged in" screen automatically. The
// host never has to add an <AuthGuard> wrapper itself; the guard ships baked
// into the page. (This is centralized here, in ONE place, not per page file.)
import withAuthGuard from './components/auth/withAuthGuard.jsx';

import AdminPageWithShell from './pages/admin/AdminPageWithShell.jsx';
import RawLegacyAdminPage from './pages/Modules/LegacyAdminPage.jsx';
import RawStickyNotesFullPage from './pages/stickyNotesFullPage.jsx';
import RawBulkUploadPage from './pages/BulkUpload/index.jsx';

import RawTestForm from './pages/TestingPage/test-form.jsx';
import RawZinnextTestForm from './pages/TestingPage/ZinnextTestForm.jsx';
import RawManageModules from './pages/TestingPage/ManageModules.jsx';
import RawManageSubmodules from './pages/TestingPage/ManageSubmodules.jsx';
import RawZinnextTableView from './pages/TestingPage/ZinnextTableView.jsx';
import RawTestingHomePage from './pages/TestingPage/testHomePage.jsx';
import RawLmsTestForm from './pages/TestingPage/lms-test-form.jsx';
import RawLmsHomePage from './pages/TestingPage/lmsHomePage.jsx';

export const AdminPage = AdminPageWithShell;
export const LegacyAdminPage = withAuthGuard(RawLegacyAdminPage);
export const StickyNotesFullPage = withAuthGuard(RawStickyNotesFullPage);
export const BulkUploadPage = withAuthGuard(RawBulkUploadPage);

// Testing-flow pages
export const TestForm = withAuthGuard(RawTestForm);
export const ZinnextTestForm = withAuthGuard(RawZinnextTestForm);
export const ManageModules = withAuthGuard(RawManageModules);
export const ManageSubmodules = withAuthGuard(RawManageSubmodules);
export const ZinnextTableView = withAuthGuard(RawZinnextTableView);
export const TestingHomePage = withAuthGuard(RawTestingHomePage);
export const LmsTestForm = withAuthGuard(RawLmsTestForm);
export const LmsHomePage = withAuthGuard(RawLmsHomePage);
export {
  createWorkflowStageListFetcher,
  createAllWorkflowsListFetcher,
  getAllWorkflowsModuleList,
  getWorkflowStageModuleList,
  stageCountKey,
  resolveMergeKey,
} from './services/workflowModuleListApi.js';
export {
  createMergedListFetcher,
  createModuleListFetchAdapter,
  defaultMergeRow,
  resolveMergeKey as resolveMergedListKey,
} from './services/mergedListApi.js';

// ── Services / config / theme (host app uses these in App.jsx) ──────────────
export {
  getStoredToken,
  getStoredUser,
  logout,
  getCurrentRoleName,
  getUserRoles,
  isTester,
  setSelectedProject,
  getSelectedProject,
  clearSelectedProject,
} from './services/authApi.js';
export {
  configureCoreTheme,
  AUTH_URL,
  ADMIN_API_URL,
  ADMIN_APP_ID,
  APP_ID,
  withAppId,
  withAdminAppId,
  SUBMISSIONS_URL,
  JOBS_URL,
  CANDIDATES_URL,
} from './services/apiConfig.js';
export { getActivity, getModuleDataDetail } from './services/detailedViewApi.js';
// Native submission approve/reject (schema-free gateway) — reused by the host's
// Finance Approval submissionManager handlers so the edited proposed rate is
// appended to proposedRate history by the same verified path the popup uses.
export { approveSubmission, rejectSubmission, getVMSApprove } from './services/popupApi.js';
export { getModuleConfig, getPeriodBadgeConfig } from './components/cards/cardConfig.js';
export { colors } from './theme/colors/colors.js';

// Shared formatting + condition helpers. Exported so a HOST app's detail/header
// config formats money, rate units and conditional labels through exactly the
// same rules the package's own surfaces use — before this, each host kept its
// own copy and the two drifted (see configUtils.js in ZINNEXT-V2).
export {
  CURRENCY_SYMBOLS,
  currencySymbol,
  rateUnitLabel,
  rateUnitShort,
  toNumber,
  formatAmount,
  formatMoney,
  formatRate,
} from './utils/currency.js';
export {
  conditionMatches,
  configuredFieldLabel,
  showIfConditions,
  evaluateShowIfWith,
} from './components/form/conditionalFieldLabel.js';
